import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'

import CostCentersTable from './components/CostCentersTable'
import CreateCostCenterDialog from './components/CreateCostCenterDialog'
import RenameCostCenterDialog from './components/RenameCostCenterDialog'

import styles from './CostCentersView.module.scss'

import { isApiError } from '@/api'
import { createCostCenterBilling, listCostCenters } from '@/api/costCenters'
import type { CostCenterResponse } from '@/api/generated'
import FormErrorBanner from '@/components/FormErrorBanner'
import { useToast } from '@/components/Toast/ToastContext'

export const COST_CENTERS_QUERY_KEY = (enterpriseId: string) => ['cost-centers', enterpriseId] as const

interface CostCentersViewProps {
  enterpriseId: string
}

const CostCentersView = ({ enterpriseId }: CostCentersViewProps) => {
  const [isCreating, setIsCreating] = useState(false)
  const [renaming, setRenaming] = useState<CostCenterResponse | null>(null)
  const { updateToast } = useToast()

  const {
    data: costCenters = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: COST_CENTERS_QUERY_KEY(enterpriseId),
    queryFn: async () => (await listCostCenters(enterpriseId)).data.cost_centers,
    // The CC list is the entry point to break-away — staleness here is more
    // confusing than the cost of a refetch. Force a fresh fetch every mount
    // so the table reflects current state even if an earlier visit cached
    // an empty result (e.g. during a backend restart, or before the user's
    // sign-in cookie was attached to the request).
    refetchOnMount: 'always',
  })

  const resumeCheckoutMutation = useMutation({
    mutationFn: async ({ cc, placeholder }: { cc: CostCenterResponse; placeholder: Window | null }) => {
      try {
        const res = await createCostCenterBilling(cc.id)
        return { url: res.data.url, placeholder }
      } catch (err) {
        placeholder?.close()
        throw err
      }
    },
    onSuccess: ({ url, placeholder }) => {
      // Popup blockers only treat window.open as user-initiated when called
      // synchronously inside the click handler. By the time onSuccess fires
      // (post-await), the gesture window has closed and most browsers will
      // silently block the popup. The handler opens a placeholder window up
      // front; here we just point it at the Stripe URL.
      if (placeholder && !placeholder.closed) {
        placeholder.location.href = url
      } else {
        // Placeholder was blocked or closed; fall back and accept the popup
        // warning on a small fraction of browsers.
        window.open(url, '_blank', 'noopener,noreferrer')
      }
    },
    onError: (err: unknown) => {
      const detail = isApiError(err) ? (err.detail ?? err.message) : 'Failed to resume checkout'
      updateToast(detail, 'error')
    },
  })

  const handleResumeCheckout = (cc: CostCenterResponse) => {
    // Open the placeholder synchronously, inside the click event, so the
    // browser counts it as user-initiated before we go async to mint the
    // Stripe session URL.
    const placeholder = window.open('about:blank', '_blank')
    resumeCheckoutMutation.mutate({ cc, placeholder })
  }

  return (
    <div className={styles.container}>
      {error && <FormErrorBanner message="Failed to load cost centers" />}

      <CostCentersTable
        costCenters={costCenters}
        isLoading={isLoading}
        onCreate={() => setIsCreating(true)}
        onRename={setRenaming}
        onResumeCheckout={handleResumeCheckout}
        resumingCostCenterId={
          resumeCheckoutMutation.isPending ? (resumeCheckoutMutation.variables?.cc.id ?? null) : null
        }
      />

      <CreateCostCenterDialog
        isOpen={isCreating}
        enterpriseId={enterpriseId}
        onSave={() => setIsCreating(false)}
        onCancel={() => setIsCreating(false)}
      />

      <RenameCostCenterDialog
        isOpen={!!renaming}
        costCenter={renaming}
        enterpriseId={enterpriseId}
        onClose={() => setRenaming(null)}
      />
    </div>
  )
}

export default CostCentersView
